import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useAuthApi } from "../../hooks/api/axiosInterceptor";

const Wrapper = styled.div`
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
`;

const FormTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 30px;
`;

const TableRow = styled.tr`
    border-bottom: 1px solid #e0e0e0;
    
    &:first-child {
        border-top: 2px solid #666;
    }
`;

const TableHeader = styled.th`
    width: 30%;
    padding: 15px 10px;
    background-color: #f9f9f9;
    text-align: left;
    font-weight: 600;
`;

const TableCell = styled.td`
    padding: 15px 10px;
`;

const Input = styled.input`
    padding: 8px 12px;
    border: 1px solid #ddd;
    width: ${props => props.width || "100%"};
    max-width: ${props => props.maxWidth || "none"};
`;

const AddressWrapper = styled.div`
    margin-bottom: 5px;
`;

const AddressButton = styled.button`
    padding: 8px 15px;
    background-color: #2056ad;
    color: white;
    border: none;
    margin-left: 5px;
    cursor: pointer;
    
    &:hover {
        background-color: #1a4786;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 40px;
    margin-bottom: 140px;
`;

const SubmitButton = styled.button`
    width: 100px;
    padding: 12px 25px;
    background-color: #2056ad;
    color: white;
    border: none;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
        background-color: #1a4786;
    }
`;

const ErrorMessage = styled.div`
    color: #e74c3c;
    font-size: 14px;
    margin-top: 5px;
`;

const PhoneContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const HelpText = styled.span`
    font-size: 14px;
    color: #888;
    margin-left: 8px;
`;

// 다음 우편번호 검색 모달
const PostcodeModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const PostcodeContainer = styled.div`
    position: relative;
    width: 500px;
    height: 500px;
    background-color: white;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
    border-radius: 4px;
    overflow: hidden;
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    border: none;
    background-color:rgb(110, 110, 110);
    color: white;
    border-radius: 50%;
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1001;
    
    &:hover {
        background-color:rgb(75, 75, 75);
    }
`;

const ModifyPersonalInfo = () => {
    const [userData, setUserData] = useState({
        name: '',
        userId: '',
        password: '',
        confirmPassword: '',
        zipCode: '',
        address1: '',
        address2: '',
        phone1: '',
        phone2: '',
        phone3: '',
        email: '',
        backupEmail: ''
    });
    
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [showPostcode, setShowPostcode] = useState(false);
    const postcodeContainerRef = useRef(null);
    const daumScriptLoaded = useRef(false);
    
    useEffect(() => {
        // 사용자 데이터 로드
        const fetchUserData = async () => {
            try {
                setIsLoading(true);
                const response = await useAuthApi.get('/user/profile');
                
                if (response.data.result === 'SUCCESS') {
                    const data = response.data.data;
                    // 전화번호 분리 (백엔드에서 하나의 문자열로 제공한다고 가정)
                    const phoneNumber = data.phone || '';
                    const phoneNumbers = phoneNumber.split('-');
                    
                    setUserData({
                        name: data.name || '',
                        userId: data.userId || '',
                        password: '',
                        confirmPassword: '',
                        zipCode: data.zipCode || '',
                        address1: data.address1 || '',
                        address2: data.address2 || '',
                        phone1: phoneNumbers[0] || '',
                        phone2: phoneNumbers[1] || '',
                        phone3: phoneNumbers[2] || '',
                        email: data.email || '',
                        backupEmail: data.backupEmail || ''
                    });
                } else {
                    console.error("사용자 데이터 로드 실패:", response.data.message);
                }
            } catch (error) {
                console.error("API 요청 오류:", error);
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchUserData();
        
        // Daum 우편번호 스크립트 로드
        const loadDaumPostcodeScript = () => {
            if (!document.getElementById('daum-postcode-script')) {
                const script = document.createElement('script');
                script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
                script.id = 'daum-postcode-script';
                script.async = true;
                script.onload = () => {
                    daumScriptLoaded.current = true;
                };
                document.head.appendChild(script);
            }
        };
        
        loadDaumPostcodeScript();
        
        // 컴포넌트 언마운트 시 이벤트 리스너 정리
        return () => {
        };
    }, []);
    
    useEffect(() => {
        // 우편번호 모달이 열릴 때 초기화
        if (showPostcode && daumScriptLoaded.current) {
            initPostcode();
        }
    }, [showPostcode]);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData(prevData => ({
            ...prevData,
            [name]: value
        }));
        
        // 에러 메시지 초기화
        if (errors[name]) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: ''
            }));
        }
    };
    
    const openPostcode = () => {
        setShowPostcode(true);
    };
    
    const closePostcode = () => {
        setShowPostcode(false);
    };
    
    const initPostcode = () => {
        if (!postcodeContainerRef.current || !window.daum || !window.daum.Postcode) {
            console.log("Postcode container or Daum script not yet loaded");
            return;
        }
        
        // 모달이 열릴 때마다 새로운 인스턴스 생성
        new window.daum.Postcode({
            oncomplete: handlePostcodeComplete,
            width: '100%',
            height: '100%'
        }).embed(postcodeContainerRef.current);
    };
    
    const handlePostcodeComplete = (data) => {
        setUserData(prevData => ({
            ...prevData,
            zipCode: data.zonecode,
            address1: data.roadAddress || data.jibunAddress
        }));
        closePostcode();
    };
    
    const validateForm = () => {
        const newErrors = {};
        
        // 비밀번호 유효성 검사
        if (userData.password) {
            if (userData.password.length < 4 || userData.password.length > 12) {
                newErrors.password = '비밀번호를 4~12자 미만으로 입력해주세요.';
            } else if (!/^[a-zA-Z0-9]+$/.test(userData.password)) {
                newErrors.password = '비밀번호는 영문자, 숫자만 사용하실 수 있습니다.';
            }
        } else {
            newErrors.password = '패스워드를 입력해주세요';
        }
        
        // 비밀번호 확인
        if (userData.password !== userData.confirmPassword) {
            newErrors.confirmPassword = '비밀번호가 일치하지 않습니다. 비밀번호를 다시 입력해주세요.';
        }
        
        // 주소 확인
        if (!userData.zipCode) {
            newErrors.zipCode = '우편번호 찾기를 클릭해주세요';
        }
        
        if (!userData.address2) {
            newErrors.address2 = '세부주소를 입력해주세요';
        }
        
        // 연락처 확인
        if (!userData.phone1 || !userData.phone2 || !userData.phone3) {
            newErrors.phone = '연락처를 입력해주세요';
        }
        
        // 이메일 확인
        if (!userData.email) {
            newErrors.email = '이메일을 입력해주세요';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }
        
        try {
            const response = await useAuthApi.put('/user/update', {
                password: userData.password,
                zipCode: userData.zipCode,
                address1: userData.address1,
                address2: userData.address2,
                phone: `${userData.phone1}-${userData.phone2}-${userData.phone3}`,
                email: userData.email,
                backupEmail: userData.backupEmail
            });
            
            if (response.data.result === 'SUCCESS') {
                alert('개인 정보가 성공적으로 수정되었습니다.');
            } else {
                alert(`정보 수정 실패: ${response.data.message}`);
            }
        } catch (error) {
            console.error("API 요청 오류:", error);
            alert('개인 정보 수정에 실패했습니다.');
        }
    };
    
    const handleCancel = () => {
        // 뒤로가기
        window.history.back();
    };
    
    if (isLoading) {
        return <Wrapper><p>로딩 중...</p></Wrapper>;
    }
    
    return (
        <Wrapper>
            <FormTable>
                <tbody>
                    <TableRow>
                        <TableHeader>이름</TableHeader>
                        <TableCell>{userData.name}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHeader>회원아이디</TableHeader>
                        <TableCell>{userData.userId}</TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHeader>비밀번호</TableHeader>
                        <TableCell>
                            <Input 
                                type="password" 
                                name="password" 
                                value={userData.password} 
                                onChange={handleChange} 
                                maxLength={20} 
                                maxWidth="300px"
                            />
                            <br />
                            <HelpText>4~12자 미만의 영문 / 숫자</HelpText>
                            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHeader>비밀번호 재입력</TableHeader>
                        <TableCell>
                            <Input 
                                type="password" 
                                name="confirmPassword" 
                                value={userData.confirmPassword} 
                                onChange={handleChange} 
                                maxLength={20} 
                                maxWidth="300px"
                            />
                            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword}</ErrorMessage>}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHeader>주소</TableHeader>
                        <TableCell>
                            <AddressWrapper>
                                <Input 
                                    type="text" 
                                    name="zipCode" 
                                    value={userData.zipCode} 
                                    readOnly 
                                    width="100px" 
                                />
                                <AddressButton type="button" onClick={openPostcode}>우편번호</AddressButton>
                                <Input 
                                    type="text" 
                                    name="address1" 
                                    value={userData.address1} 
                                    readOnly 
                                    style={{ marginTop: '5px' }}
                                />
                            </AddressWrapper>
                            <Input 
                                type="text" 
                                name="address2" 
                                value={userData.address2} 
                                onChange={handleChange} 
                                style={{ marginTop: '5px' }}
                            />
                            {errors.zipCode && <ErrorMessage>{errors.zipCode}</ErrorMessage>}
                            {errors.address2 && <ErrorMessage>{errors.address2}</ErrorMessage>}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHeader>연락처</TableHeader>
                        <TableCell>
                            <PhoneContainer>
                                <Input 
                                    type="text" 
                                    name="phone1" 
                                    value={userData.phone1} 
                                    onChange={handleChange} 
                                    maxLength={3} 
                                    width="70px"
                                />
                                <span>-</span>
                                <Input 
                                    type="text" 
                                    name="phone2" 
                                    value={userData.phone2} 
                                    onChange={handleChange} 
                                    maxLength={4} 
                                    width="70px"
                                />
                                <span>-</span>
                                <Input 
                                    type="text" 
                                    name="phone3" 
                                    value={userData.phone3} 
                                    onChange={handleChange} 
                                    maxLength={4} 
                                    width="70px"
                                />
                            </PhoneContainer>
                            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHeader>포탈이메일</TableHeader>
                        <TableCell>
                            <Input 
                                type="text" 
                                name="email" 
                                value={userData.email} 
                                onChange={handleChange} 
                                width="200px" 
                            />
                            <span>@cau.ac.kr</span>
                            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableHeader>예비이메일</TableHeader>
                        <TableCell>
                            <Input 
                                type="text" 
                                name="backupEmail" 
                                value={userData.backupEmail} 
                                onChange={handleChange} 
                                width="200px" 
                                maxLength={50}
                            />
                        </TableCell>
                    </TableRow>
                </tbody>
            </FormTable>
            
            <ButtonContainer>
                <SubmitButton type="button" onClick={handleSubmit}>수정하기</SubmitButton>
            </ButtonContainer>
            
            {showPostcode && (
                <PostcodeModal>
                    <PostcodeContainer>
                        <CloseButton onClick={closePostcode}>×</CloseButton>
                        <div 
                            ref={postcodeContainerRef}
                            style={{ width: '100%', height: '100%' }}
                        ></div>
                    </PostcodeContainer>
                </PostcodeModal>
            )}
        </Wrapper>
    );
};

export default ModifyPersonalInfo;